import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { AppButtonComponent } from "../../../../Common/Components/Button/ButtonComponent";
import { AppCardComponent } from "../../../../Common/Components/Card/CardComponent";
import { AutocompleteComponent } from "../../../../Common/Components/Autocomplete/AutocompleteComponent";
import { AutocompleteOption } from "../../../../Common/Components/Autocomplete/Type/AutocompleteTypes";
import { LocationSelectorComponent } from "../../../../Common/Components/LocationSelector/LocationSelectorComponent";
import { RichTextEditorComponent } from "../../../../Common/Components/RichTextEditor/RichTextEditorComponent";
import { ToastEffects } from "../../../../Common/Components/Toast/State/ToastEffects";
import { ShipmentEffects } from "../../State/ShipmentEffects";
import { ShipmentBox, ShipmentEntity, ShipmentRequestSummary } from "../../Dto/ShipmentModel";
import { LocationAddress } from "../../../../Common/Components/LocationSelector/Type/LocationSelectorTypes";
import { ConfirmModalEffects } from "../../../../Common/Components/ConfirmModal/State/ConfirmModalEffects";
import { getBackendErrorMessage } from "../../../../Common/Utility/UtilityError";
import { AppSummaryPanelComponent, SummaryPanelRow } from "../../../../Common/Components/SummaryPanel/SummaryPanelComponent";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { TableColumn, TableConfig, TablePagination } from "../../../../Common/Components/Table/Type/TableType";
import { QtyInputSelectZeroDirective } from "../../../../Common/Components/QtyInput/QtyInputSelectZeroDirective";
import { AllocationRow, MeasurementDraft, ShipmentStage } from "../../Dto/ShipmentPageModel";
import { ShipmentBoxExpandedRowComponent } from "../../Components/ShipmentBoxExpandedRow/ShipmentBoxExpandedRowComponent";
import { VendorApi } from "../../../Vendor/Api/VendorApi";
import { VendorType } from "../../../Vendor/Dto/VendorModel";
import { FileUploaderComponent } from "../../../File/Components/FileUploader/FileUploaderComponent";
import { FileApi } from "../../../File/Api/FileApi";
import { File as UploadedFile } from "../../../File/Dto/FileModel";
import { ShipmentLeg } from "../../Dto/ShipmentModel";
import { PartnerApi } from "../../../Partner/Api/PartnerApi";
import { Partner } from "../../../Partner/Dto/PartnerModel";
import { TeamLeaderApi } from "../../../TeamLeader/Api/TeamLeaderApi";
import { TeamLeaderListItem } from "../../../TeamLeader/Dto/TeamLeaderModel";
import {
  buildShipmentDestinationSnapshot,
  createEmptyDestinationSection,
  createEmptyShipmentAddress,
  createSeededDestinationSection,
  formatShipmentAddress,
  isShipmentAddressEmpty,
} from "../Shared/ShipmentDestinationForm";

@Component({
  selector: "app-shipment-edit",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppButtonComponent,
    AppCardComponent,
    AutocompleteComponent,
    LocationSelectorComponent,
    AppSummaryPanelComponent,
    AppTableComponent,
    QtyInputSelectZeroDirective,
    RichTextEditorComponent,
    ShipmentBoxExpandedRowComponent,
    FileUploaderComponent,
  ],
  templateUrl: "./ShipmentEditPage.html",
  styleUrl: "./ShipmentEditPage.scss",
})
export class ShipmentEditPage implements OnInit {
  private readonly orderedStages: ShipmentStage[] = ["snapshot", "pack", "dispatch", "delivery"];
  readonly attachmentFolder = "shipments";
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly shipmentEffects = inject(ShipmentEffects);
  private readonly toastEffects = inject(ToastEffects);
  private readonly confirmModalEffects = inject(ConfirmModalEffects);
  private readonly vendorApi = inject(VendorApi);
  private readonly fileApi = inject(FileApi);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly partnerApi = inject(PartnerApi);
  private readonly teamLeaderApi = inject(TeamLeaderApi);

  readonly shipmentId = this.route.snapshot.paramMap.get("id") ?? undefined;
  shipment: ShipmentEntity | null = null;
  isReady = false;
  isSaving = false;
  isDispatchAttachmentSaving = false;
  editingBoxId: string | null = null;
  isCreatingBoxDraft = false;
  expandedRequestIds = new Set<string>();
  allocationRows: AllocationRow[] = [];
  activeStage: ShipmentStage = "snapshot";
  measurementDrafts = new Map<string, MeasurementDraft>();
  savedBoxesData: ShipmentBox[] = [];
  vendorOptions: AutocompleteOption[] = [];
  finalPartnerOptions: AutocompleteOption[] = [];
  finalRecipientOptions: AutocompleteOption[] = [];
  transitPartnerOptions: AutocompleteOption[] = [];
  transitRecipientOptions: AutocompleteOption[] = [];
  selectedDispatchVendorId = "";
  selectedFinalLegVendorId = "";
  selectedFinalPartnerId = "";
  selectedFinalPartner: Partner | null = null;
  selectedFinalRecipientId = "";
  selectedFinalRecipient: TeamLeaderListItem | null = null;
  finalPhoneValue = "";
  finalAddressValue: LocationAddress = this.createEmptyAddress();
  finalRecipientsAvailable: boolean | null = null;
  selectedTransitPartnerId = "";
  selectedTransitPartner: Partner | null = null;
  selectedTransitRecipientId = "";
  selectedTransitRecipient: TeamLeaderListItem | null = null;
  transitPhoneValue = "";
  transitAddressValue: LocationAddress = this.createEmptyAddress();
  transitRecipientsAvailable: boolean | null = null;
  hasTransitStop = false;
  finalSelectionTouched = false;
  transitSelectionTouched = false;
  private readonly finalPartnerMap = new Map<string, Partner>();
  private readonly finalRecipientMap = new Map<string, TeamLeaderListItem>();
  private readonly transitPartnerMap = new Map<string, Partner>();
  private readonly transitRecipientMap = new Map<string, TeamLeaderListItem>();
  hasSavedBoxes = false;
  savedBoxesPagination: TablePagination = {
    page: 1,
    limit: 1,
    total: 0,
    pages: 0,
  };
  @ViewChild("savedBoxExpandedRow", { static: true }) savedBoxExpandedRowTemplate!: TemplateRef<{ $implicit: ShipmentBox }>;

  readonly form = this.fb.group({
    note: [""],
  });

  readonly transitForm = this.fb.group({
    transitPartnerId: [""],
    transitRecipientTeamLeaderId: [""],
  });

  readonly boxForm = this.fb.group({
    sequenceNo: [1, [Validators.required, Validators.min(1)]],
    note: [""],
    allocations: this.fb.array([]),
  });

  readonly dispatchForm = this.fb.group({
    vendorId: ["", [Validators.required]],
    finalLegVendorId: [""],
    waybillNo: ["", [Validators.required]],
    note: [""],
    attachments: this.fb.control<UploadedFile[]>([], { nonNullable: true }),
  });

  readonly deliverForm = this.fb.group({
    receivedBy: [""],
    note: [""],
  });

  get allocations(): FormArray {
    return this.boxForm.get("allocations") as FormArray;
  }

  get isTransitShipment(): boolean {
    return this.hasTransitStop;
  }

  get currentLeg(): ShipmentLeg | null {
    return this.shipment?.activeLeg ?? this.shipment?.legs?.[0] ?? null;
  }

  get transitLeg(): ShipmentLeg | null {
    return this.shipment?.legs?.find((leg) => leg.type === "Transit") ?? null;
  }

  get finalLeg(): ShipmentLeg | null {
    return this.shipment?.legs?.find((leg) => leg.type === "Final") ?? this.currentLeg;
  }

  get isTransitDispatchRoute(): boolean {
    return Boolean(this.transitLeg && this.finalLeg && (this.shipment?.legs?.length ?? 0) > 1);
  }

  get isTransitFirstDispatch(): boolean {
    return this.shipment?.status === "Packed" && this.currentLeg?.type === "Transit";
  }

  get isFinalLegDispatch(): boolean {
    return this.shipment?.status === "Packed" && this.currentLeg?.type === "Final" && (this.shipment?.legs?.length ?? 0) > 1;
  }

  get isTransitArrivalPending(): boolean {
    return (() => {
      if (this.shipment) {
        return this.isTransitArrivalPendingForShipment(this.shipment);
      }
      return false;
    })();
  }

  get hasRouteStarted(): boolean {
    return (this.shipment?.legs ?? []).some((leg) => leg.status !== "Pending");
  }

  allocationControlAt(index: number): FormGroup {
    return this.allocations.at(index) as FormGroup;
  }

  get isEditableShipment(): boolean {
    return Boolean((this.shipment?.status === "Draft" || this.shipment?.status === "Packed") && !this.hasRouteStarted);
  }

  get canEditSnapshot(): boolean {
    return this.isEditableShipment;
  }

  get isDeliveredShipment(): boolean {
    return this.shipment?.status === "Delivered";
  }

  get isSnapshotHistoryMode(): boolean {
    return Boolean(this.shipment && !this.canEditSnapshot);
  }

  get isSnapshotValid(): boolean {
    const finalSnapshot = this.getCurrentFinalSnapshot();
    const finalPartnerId = this.resolveFinalPartnerPayload();
    const finalRecipientTeamLeaderId = this.resolveFinalRecipientPayload();

    if (
      !(
        finalPartnerId &&
        finalRecipientTeamLeaderId &&
        this.finalPhoneValue.trim() &&
        finalSnapshot?.destinationAddress &&
        !this.isLocationAddressEmpty(finalSnapshot.destinationAddress) &&
        this.finalRecipientsAvailable !== false
      )
    ) {
      return false;
    }

    if (!this.isTransitShipment) {
      return true;
    }

    const transitSnapshot = this.getCurrentTransitSnapshot();
    const transitPartnerId = this.resolveTransitPartnerPayload();
    const transitRecipientTeamLeaderId = this.resolveTransitRecipientPayload();

    return Boolean(
      transitPartnerId &&
      transitRecipientTeamLeaderId &&
      transitSnapshot?.recipientPhone?.trim() &&
      transitSnapshot?.destinationAddress &&
      !this.isLocationAddressEmpty(transitSnapshot.destinationAddress),
    );
  }

  get canDispatch(): boolean {
    return this.shipment?.canDispatch ?? false;
  }

  get canSubmitDispatch(): boolean {
    return (
      this.canDispatch &&
      this.dispatchForm.valid &&
      (!this.isTransitFirstDispatch || !!this.dispatchForm.value.finalLegVendorId?.trim()) &&
      (!this.isFinalLegDispatch || !!this.finalLeg?.vendorId?.trim()) &&
      !this.isSaving &&
      !this.isDispatchAttachmentSaving
    );
  }

  get canDeliver(): boolean {
    return this.shipment?.status === "Dispatched";
  }

  get hasBoxes(): boolean {
    return (this.shipment?.boxes?.length ?? 0) > 0;
  }

  get hasActiveBoxWorkspace(): boolean {
    return this.isCreatingBoxDraft || !!this.editingBoxId;
  }

  get activeBox(): ShipmentBox | null {
    if (!this.shipment || !this.editingBoxId) {
      return null;
    }
    return this.shipment.boxes?.find((box) => box.id === this.editingBoxId) ?? null;
  }

  get packWorkspaceTitle(): string {
    if (this.isDeliveredShipment) {
      return "Packed Boxes History";
    }
    if (this.activeBox) {
      return `Pack Into ${this.activeBox.boxNo}`;
    }
    if (this.isCreatingBoxDraft) {
      return "Pack Into New Box";
    }
    if (this.hasBoxes) {
      return "Pack Into a Box";
    }
    return "Create the First Box to Start Packing";
  }

  get packWorkspaceCopy(): string {
    if (this.isDeliveredShipment) {
      return "Boxes and allocations are locked. This step is available as shipment history only.";
    }
    if (this.activeBox || this.isCreatingBoxDraft) {
      return "Assign the remaining request quantities first. You can measure the saved box after it is physically packed.";
    }
    if (this.hasBoxes) {
      return "Create a new packing draft here, or reopen a saved box to keep assigning items into it.";
    }
    return "Start by creating the first box. After that, assign items into saved boxes first and measure them later.";
  }

  get measuredBoxCount(): number {
    return (this.shipment?.boxes ?? []).filter((box) => this.isBoxMeasured(box)).length;
  }

  get boxesNeedingMeasurementCount(): number {
    return (this.shipment?.boxes ?? []).filter((box) => !this.isBoxMeasured(box)).length;
  }

  get hasBoxesNeedingMeasurement(): boolean {
    return this.boxesNeedingMeasurementCount > 0;
  }

  get shipmentPartnerLabel(): string {
    const partnerNames = Array.from(
      new Set((this.shipment?.requests ?? []).map((request) => request.partnerName).filter((name) => !!name?.trim())),
    );

    if (partnerNames.length === 0) {
      return this.shipment?.destinationInstitutionName || "-";
    }

    if (partnerNames.length === 1) {
      return partnerNames[0];
    }

    return `${partnerNames[0]} +${partnerNames.length - 1} more`;
  }

  get packingSummaryRows(): SummaryPanelRow[] {
    if (!this.shipment) {
      return [];
    }

    return [
      { label: "Requests", value: this.shipment.requestCount },
      { label: "Boxes", value: this.shipment.boxCount },
      { label: "Unboxed", value: this.shipment.totalRequestedQty - this.shipment.totalBoxedQty },
      { label: "Packed", value: this.shipment.totalBoxedQty },
      { label: "Needs Measure", value: this.boxesNeedingMeasurementCount },
      { label: "Measured Boxes", value: this.measuredBoxCount },
      { label: "Next Action", value: this.shipment.nextAction },
    ];
  }

  get savedBoxColumns(): TableColumn<ShipmentBox>[] {
    return [
      {
        key: "boxNo",
        header: "Box",
        width: "72px",
        cellTemplate: (box) => box.boxNo,
        cellClass: "app-table__cell-text--strong",
      },
      {
        key: "status",
        header: "Status",
        width: "170px",
        cellTemplate: (box) => this.getSavedBoxMeasurementStatus(box),
        cellClass: (box) => this.getSavedBoxMeasurementStatusClass(box),
      },
      {
        key: "owner",
        header: "Owner",
        width: "180px",
        cellTemplate: (box) => this.getBoxOwnerSummary(box),
        cellClass: "app-table__cell-text--muted",
      },
      {
        key: "packedQty",
        header: "Packed",
        width: "96px",
        align: "left",
        cellTemplate: (box) => this.getTotalAllocatedQty(box),
        cellClass: "app-table__cell-text--mono",
      },
      {
        key: "lengthCm",
        header: "Length (cm)",
        width: "110px",
        align: "left",
        editable: () => this.isEditableShipment,
        editType: "number",
        saveKey: "lengthCm",
        editValue: (box) => this.getMeasurementDraftValue(box, "lengthCm"),
        min: 1,
        step: 1,
      },
      {
        key: "widthCm",
        header: "Width (cm)",
        width: "110px",
        align: "left",
        editable: () => this.isEditableShipment,
        editType: "number",
        saveKey: "widthCm",
        editValue: (box) => this.getMeasurementDraftValue(box, "widthCm"),
        min: 1,
        step: 1,
      },
      {
        key: "heightCm",
        header: "Height (cm)",
        width: "110px",
        align: "left",
        editable: () => this.isEditableShipment,
        editType: "number",
        saveKey: "heightCm",
        editValue: (box) => this.getMeasurementDraftValue(box, "heightCm"),
        min: 1,
        step: 1,
      },
      {
        key: "weightKg",
        header: "Weight (kg)",
        width: "110px",
        align: "right",
        editable: () => this.isEditableShipment,
        editType: "number",
        saveKey: "weightKg",
        editValue: (box) => this.getMeasurementDraftValue(box, "weightKg"),
        min: 0.1,
        step: 0.1,
      },
      {
        key: "cubicM3",
        header: "Cubic (m3)",
        width: "110px",
        align: "left",
        cellTemplate: (box) => this.formatCubicPreview(box),
        cellClass: "app-table__cell-text--mono",
      },
    ];
  }

  get savedBoxConfig(): TableConfig<ShipmentBox> {
    return {
      emptyMessage: "No boxes yet. Start by creating the first box in the section above, then saved boxes will appear here.",
      showActions: true,
      showPagination: false,
      actionsWidth: "210px",
      inlineEdit: {
        enabled: true,
        getRowId: (box) => box.id,
        isRowEditing: () => true,
        getDraftValue: (box, column) => this.getMeasurementDraftValue(box, column.saveKey as keyof MeasurementDraft),
        setDraftValue: (box, column, value) => this.setMeasurementDraftValue(box, column.saveKey as keyof MeasurementDraft, value),
      },
      expandable: {
        enabled: true,
        allowMultipleExpanded: true,
        getRowId: (box) => box.id,
        expandedRowTemplate: this.savedBoxExpandedRowTemplate,
        expandButtonLabel: "Show box contents",
      },
      actionButtons: [
        {
          icon: "sell",
          label: "Open Label PDF",
          handler: (box) => void this.openLabelPdf(box),
          disabled: (box) => this.isSaving || !this.canOpenLabel(box),
        },
        {
          icon: "save",
          label: "Save Measurements",
          handler: (box) => void this.saveMeasurements(box),
          visible: () => this.isEditableShipment,
          disabled: (box) => this.isSaving || !this.canSaveMeasurements(box),
        },
        {
          icon: "edit",
          label: "Edit Box Contents",
          handler: (box) => this.editBox(box),
          visible: () => this.isEditableShipment,
          disabled: () => this.isSaving,
        },
        {
          icon: "delete",
          label: "Remove Box",
          handler: (box) => this.deleteBox(box),
          visible: () => this.isEditableShipment,
          disabled: () => this.isSaving,
        },
      ],
    };
  }

  get operationsSummaryRows(): SummaryPanelRow[] {
    if (!this.shipment) {
      return [];
    }

    return [
      { label: "Total Requested", value: this.shipment.totalRequestedQty },
      { label: "Boxes", value: this.shipment.boxCount },
      { label: "Unboxed", value: this.shipment.totalRequestedQty - this.shipment.totalBoxedQty },
      { label: "Packed", value: this.shipment.totalBoxedQty },
      { label: "Total Dispatched", value: this.shipment.totalDispatchedQty },
      { label: "Total Delivered", value: this.shipment.totalDeliveredQty },
      { label: "Remaining", value: this.shipment.totalRemainingQty },
      { label: "Next Action", value: this.shipment.nextAction },
    ];
  }

  async ngOnInit(): Promise<void> {
    if (!this.shipmentId) {
      this.goBack();
      return;
    }

    await this.loadShipment(this.shipmentId);
  }

  getStatusLabel(status?: ShipmentEntity["status"]): string {
    if (status === "Draft") return "Setup / Packing";
    if (status === "Packed") return "Ready to Dispatch";
    if (status === "Dispatched") return "In Transit";
    return status ?? "-";
  }

  getSnapshotSectionTitle(): string {
    return (() => {
      if (this.isSnapshotHistoryMode) {
        return "Destination Snapshot History";
      }
      return "Destination Snapshot";
    })();
  }

  getSnapshotSectionCopy(): string {
    return (() => {
      if (this.isSnapshotHistoryMode) {
        return "Review the saved destination snapshot used for this shipment.";
      }
      return "Choose partner, recipient, phone, and address.";
    })();
  }

  getDispatchSectionTitle(): string {
    if (this.isTransitDispatchRoute) {
      return "Dispatch Route";
    }

    return (() => {
      if (this.currentLeg?.type === "Transit") {
        return "Dispatch To Transit Drop Point";
      }
      return "Dispatch To Final Destination";
    })();
  }

  getDispatchSectionCopy(): string {
    if (this.isDeliveredShipment) {
      return "Review the dispatch route, vendors, waybills, notes, and attachments used for this shipment.";
    }

    if (this.currentLeg?.type === "Transit") {
      return "This is the first-leg stock-out. Select both the transit vendor and the locked final vendor before boxes leave the warehouse.";
    }

    if (this.isFinalLegDispatch) {
      return "Confirm the final-leg dispatch with the vendor that was locked before the first warehouse dispatch.";
    }

    return "Dispatch the active leg once every required quantity is boxed and every saved box is measured.";
  }

  getDispatchButtonLabel(): string {
    if (this.isTransitFirstDispatch) {
      return "Dispatch To Transit";
    }

    if (this.isFinalLegDispatch) {
      return "Dispatch To Final Destination";
    }

    return "Dispatch Shipment";
  }

  getDeliverySectionTitle(): string {
    return (() => {
      if (this.currentLeg?.type === "Transit") {
        return "Transit Receipt";
      }
      return "Delivery Receipt";
    })();
  }

  getDeliverySectionCopy(): string {
    if (this.currentLeg?.type === "Transit") {
      return "Confirm the relay point received the shipment so the final-leg dispatch can begin.";
    }

    return "Confirm who received the shipment at the final destination.";
  }

  getVisibleStages(): ShipmentStage[] {
    return this.orderedStages;
  }

  getStageLabel(stage: ShipmentStage): string {
    if (stage === "snapshot") return "Destination Snapshot";
    if (stage === "pack") return "Pack Boxes";
    if (stage === "dispatch") return "Dispatch";
    return "Delivery Receipt";
  }

  openStage(stage: ShipmentStage): void {
    if (!this.isStageEnabled(stage)) {
      return;
    }
    this.activeStage = stage;
  }

  isStageEnabled(stage: ShipmentStage): boolean {
    if (!this.shipment) {
      return stage === "snapshot" || stage === "pack";
    }

    if (this.shipment.status === "Draft") {
      return stage === "snapshot" || stage === "pack";
    }

    if (this.shipment.status === "Packed") {
      if (this.hasRouteStarted) {
        return stage === "dispatch";
      }
      return stage === "snapshot" || stage === "pack" || stage === "dispatch";
    }

    if (this.shipment.status === "Dispatched" || this.shipment.status === "Delivered") {
      if (stage === "delivery") {
        return this.isFinalDeliveryStageAvailableForShipment(this.shipment);
      }
      if (this.isTransitArrivalPendingForShipment(this.shipment)) {
        return stage === "dispatch";
      }
      return true;
    }

    return false;
  }

  addTransitStop(): void {
    if (!this.shipment || this.hasRouteStarted) {
      return;
    }

    this.hasTransitStop = true;
    this.transitSelectionTouched = true;
    this.transitAddressValue = this.createEmptyAddress();
  }

  removeTransitStop(): void {
    if (!this.shipment || this.hasRouteStarted) {
      return;
    }

    this.hasTransitStop = false;
    this.transitSelectionTouched = true;
    this.resetTransitSelection();
    this.transitForm.patchValue({ transitPartnerId: "", transitRecipientTeamLeaderId: "" }, { emitEvent: false });
  }

  onTransitAddressChange(value: LocationAddress): void {
    this.transitSelectionTouched = true;
    this.transitAddressValue = value;
  }

  goBack(): void {
    this.router.navigate(["/shipments"]).catch(() => undefined);
  }

  getTotalAllocatedQty(box: ShipmentBox): number {
    return (box.items ?? []).reduce((sum, item) => sum + Number(item.qty ?? 0), 0);
  }

  getBoxOwnerSummary(box: ShipmentBox): string {
    const owners = new Set((box.items ?? []).map((item) => item.ownerName).filter(Boolean));
    if (owners.size === 0) {
      return "-";
    }
    return Array.from(owners).join(", ");
  }

  getTotalRequestQty(request: ShipmentRequestSummary): number {
    return (request.items ?? []).reduce((sum, item) => sum + Number(item.qty ?? 0), 0);
  }

  getTotalBoxedQty(request: ShipmentRequestSummary): number {
    return (request.items ?? []).reduce((sum, item) => sum + Number(item.boxedQty ?? 0), 0);
  }

  getTotalDeliveredQty(request: ShipmentRequestSummary): number {
    return (request.items ?? []).reduce((sum, item) => sum + Number(item.deliveredQty ?? 0), 0);
  }

  getTotalRemainingQty(request: ShipmentRequestSummary): number {
    return (request.items ?? []).reduce((sum, item) => sum + Math.max(0, Number(item.qty ?? 0) - Number(item.deliveredQty ?? 0)), 0);
  }

  hasUnboxedRemaining(): boolean {
    return (this.shipment?.requests ?? []).some((request) =>
      (request.items ?? []).some((item) => Number(item.boxedQty ?? 0) < Number(item.qty ?? 0)),
    );
  }

  hasBoxContent(): boolean {
    return this.allocations.controls.some((control) => Number(control.get("qty")?.value ?? 0) > 0);
  }

  isRequestExpanded(requestId: string): boolean {
    return this.expandedRequestIds.has(requestId);
  }

  toggleRequestExpanded(requestId: string): void {
    const next = new Set(this.expandedRequestIds);
    if (next.has(requestId)) {
      next.delete(requestId);
    } else {
      next.add(requestId);
    }
    this.expandedRequestIds = next;
  }

  startNewBox(): void {
    this.isCreatingBoxDraft = true;
    this.editingBoxId = null;
    this.buildBoxEditor();
  }

  editBox(box: ShipmentBox): void {
    this.isCreatingBoxDraft = false;
    this.editingBoxId = box.id;
    this.buildBoxEditor(box);
  }

  isBoxMeasured(box: ShipmentBox | null | undefined): boolean {
    if (!box) {
      return false;
    }
    return [box.lengthCm, box.widthCm, box.heightCm, box.weightKg].every((value) => Number(value ?? 0) > 0);
  }

  canOpenLabel(box: ShipmentBox): boolean {
    return this.isBoxMeasured(box);
  }

  canOpenAllLabels(): boolean {
    return Boolean(
      this.shipment && this.hasBoxes && !this.isSaving && (this.shipment.boxes ?? []).every((box) => this.canOpenLabel(box)),
    );
  }

  getCubicM3PreviewFor(box: ShipmentBox): number {
    const draft = this.getMeasurementDraft(box);
    return (Number(draft.lengthCm ?? 0) * Number(draft.widthCm ?? 0) * Number(draft.heightCm ?? 0)) / 1000000;
  }

  async saveShipment(): Promise<void> {
    if (!this.shipmentId || !this.shipment || !this.canEditSnapshot || !this.isSnapshotValid) {
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    try {
      const currentFinalSnapshot = this.getCurrentFinalSnapshot();
      const currentTransitSnapshot = this.getCurrentTransitSnapshot();
      const updated = await this.shipmentEffects.updateShipment(this.shipmentId, {
        finalPartnerId: this.resolveFinalPartnerPayload(),
        finalRecipientTeamLeaderId: this.resolveFinalRecipientPayload(),
        destinationInstitutionName: currentFinalSnapshot?.partnerName || "",
        recipientName: currentFinalSnapshot?.recipientName || "",
        recipientPhone: this.finalPhoneValue,
        destinationAddress: this.finalAddressValue,
        transitPartnerId: this.resolveTransitPartnerPayload(),
        transitRecipientTeamLeaderId: this.resolveTransitRecipientPayload(),
        transitDestination: (() => {
          if (this.hasTransitStop && currentTransitSnapshot) {
            return {
              transitPartnerId: this.resolveTransitPartnerPayload(),
              transitRecipientTeamLeaderId: this.resolveTransitRecipientPayload(),
              destinationInstitutionName: currentTransitSnapshot.partnerName,
              recipientName: currentTransitSnapshot.recipientName,
              recipientPhone: currentTransitSnapshot.recipientPhone,
              destinationAddress: this.transitAddressValue,
            };
          }
          return null;
        })(),
        note: this.form.value.note?.trim() ?? "",
      });

      setTimeout(() => {
        this.hydrate(updated);
        this.syncFormMode();
        this.activeStage = this.resolveInitialStage(updated);
        this.toastEffects.show("Shipment snapshot updated.", "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update shipment."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  async saveBox(): Promise<void> {
    if (!this.shipmentId || !this.shipment || !this.isEditableShipment || this.boxForm.invalid) {
      return;
    }

    const stageBeforeSave = this.activeStage;
    const previousEditingBoxId = this.editingBoxId;
    const previousBoxIds = new Set((this.shipment.boxes ?? []).map((box) => box.id));
    const isUpdatingBox = !!previousEditingBoxId;

    const payloadItems = this.allocations.controls
      .map((control, index) => ({
        requestId: this.allocationRows[index]?.requestId ?? "",
        materialId: this.allocationRows[index]?.materialId ?? "",
        qty: Number(control.get("qty")?.value ?? 0),
        maxQty: this.allocationRows[index]?.availableQty ?? 0,
      }))
      .filter((item) => item.qty > 0);

    if (payloadItems.length === 0) {
      this.toastEffects.show("Allocate at least one request line into the box.", "error");
      return;
    }

    if (payloadItems.some((item) => item.qty > item.maxQty)) {
      this.toastEffects.show("One or more box quantities are above the remaining unboxed amount.", "error");
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    const payload = {
      sequenceNo: Number(this.boxForm.value.sequenceNo ?? 1),
      note: this.boxForm.value.note?.trim() ?? "",
      items: payloadItems.map((item) => ({
        requestId: item.requestId,
        materialId: item.materialId,
        qty: item.qty,
      })),
    };

    try {
      let updated: ShipmentEntity;
      if (this.editingBoxId) {
        updated = await this.shipmentEffects.updateBox(this.shipmentId, this.editingBoxId, payload);
      } else {
        updated = await this.shipmentEffects.addBox(this.shipmentId, payload);
      }

      setTimeout(() => {
        this.hydrate(updated);
        this.syncFormMode();
        const savedBox = this.findSavedBox(updated, previousEditingBoxId, previousBoxIds);
        if (savedBox) {
          if (isUpdatingBox) {
            this.editBox(savedBox);
          } else {
            this.startNewBox();
          }
        } else {
          this.isCreatingBoxDraft = false;
          this.editingBoxId = null;
        }
        let nextStage = this.resolveInitialStage(updated);
        if (stageBeforeSave === "pack") {
          nextStage = "pack";
        }
        this.activeStage = nextStage;

        let successMessage = "Box added.";
        if (isUpdatingBox) {
          successMessage = "Box updated.";
        }
        this.toastEffects.show(successMessage, "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to save box."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  deleteBox(box: ShipmentBox): void {
    if (!this.shipmentId) {
      return;
    }

    const shouldResetToDraftWorkspace = this.activeStage === "pack" && (this.editingBoxId === box.id || this.isCreatingBoxDraft);

    this.confirmModalEffects.open({
      title: "Remove Box",
      message: `Remove box "${box.boxNo}" from this shipment?`,
      confirmText: "Remove Box",
      cancelText: "Keep Box",
      variant: "danger",
      closeOnOverlay: true,
      onConfirm: async () => {
        const stageBeforeDelete = this.activeStage;
        try {
          const updated = await this.shipmentEffects.deleteBox(this.shipmentId!, box.id);
          setTimeout(() => {
            if (shouldResetToDraftWorkspace) {
              this.editingBoxId = null;
              this.isCreatingBoxDraft = true;
            }
            this.hydrate(updated);
            this.syncFormMode();
            let nextStage = this.resolveInitialStage(updated);
            if (stageBeforeDelete === "pack") {
              nextStage = "pack";
            }
            this.activeStage = nextStage;
            this.toastEffects.show("Box removed.", "success");
            this.cdr.detectChanges();
          }, 0);
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Unable to remove box."), "error");
        }
      },
    });
  }

  async saveMeasurements(box: ShipmentBox): Promise<void> {
    if (!this.shipmentId || !this.shipment || !this.isEditableShipment || !this.canSaveMeasurements(box)) {
      return;
    }

    const stageBeforeSave = this.activeStage;
    const draft = this.getMeasurementDraft(box);
    this.isSaving = true;
    this.cdr.detectChanges();
    const payload = {
      sequenceNo: Number(box.sequenceNo ?? 1),
      lengthCm: Number(draft.lengthCm ?? 0),
      widthCm: Number(draft.widthCm ?? 0),
      heightCm: Number(draft.heightCm ?? 0),
      weightKg: Number(draft.weightKg ?? 0),
      note: box.note ?? "",
      items: (box.items ?? []).map((item) => ({
        requestId: item.requestId,
        materialId: item.materialId,
        qty: Number(item.qty ?? 0),
      })),
    };

    try {
      const updated = await this.shipmentEffects.updateBox(this.shipmentId, box.id, payload);

      setTimeout(() => {
        this.hydrate(updated);
        this.syncFormMode();
        let nextStage = this.resolveInitialStage(updated);
        if (stageBeforeSave === "pack") {
          nextStage = "pack";
        }
        this.activeStage = nextStage;
        this.toastEffects.show("Box measurements updated.", "success");
        this.cdr.detectChanges();
      }, 0);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to save box measurements."), "error");
    } finally {
      setTimeout(() => {
        this.isSaving = false;
        this.cdr.detectChanges();
      }, 0);
    }
  }

  dispatchShipment(): void {
    if (!this.shipmentId || !this.canSubmitDispatch) {
      return;
    }

    this.confirmModalEffects.open({
      title: "Dispatch Shipment",
      message: `Dispatch shipment "${this.shipment?.shipmentNo}" and deduct the packed stock from warehouse inventory?`,
      confirmText: "Dispatch Shipment",
      cancelText: "Not Yet",
      variant: "primary",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          const dispatchValue = this.dispatchForm.getRawValue();
          const updated = await this.shipmentEffects.dispatchShipment(this.shipmentId!, {
            vendorId: this.getDispatchVendorIdForPayload(),
            finalLegVendorId: (() => {
              if (this.isTransitFirstDispatch) {
                return dispatchValue.finalLegVendorId?.trim() || undefined;
              }
              return undefined;
            })(),
            waybillNo: dispatchValue.waybillNo?.trim() ?? "",
            note: dispatchValue.note?.trim() ?? "",
          });

          setTimeout(() => {
            this.hydrate(updated);
            this.syncFormMode();
            this.activeStage = this.resolveInitialStage(updated);
            this.toastEffects.show("Shipment dispatched and stock moved out.", "success");
            this.cdr.detectChanges();
          }, 0);
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Unable to dispatch shipment."), "error");
        }
      },
    });
  }

  getDispatchAttachmentHint(): string {
    if (this.shipment?.status === "Packed") {
      return "Upload shipping proof or related files here. Attachment changes save immediately before dispatch.";
    }

    if (this.canEditDispatchDocumentation) {
      return (() => {
        if (this.shipment?.status === "Delivered") {
          return "Dispatch attachments stay editable after delivery. Changes save immediately.";
        }
        return "Dispatch attachments stay editable after dispatch. Changes save immediately.";
      })();
    }

    return "Dispatch attachments are read-only for this shipment status.";
  }

  get canEditDispatchDocumentation(): boolean {
    return this.shipment?.status === "Packed" || this.shipment?.status === "Dispatched" || this.shipment?.status === "Delivered";
  }

  isDispatchAttachmentUploaderDisabled(): boolean {
    if (!this.canEditDispatchDocumentation) {
      return true;
    }

    if (this.isDispatchAttachmentSaving) {
      return true;
    }

    return false;
  }

  async onDispatchAttachmentsChange(attachments: UploadedFile[]): Promise<void> {
    if (!this.shipmentId || !this.canEditDispatchDocumentation || this.isDispatchAttachmentSaving) {
      return;
    }

    const previousAttachments = this.shipment?.attachments ?? [];
    const previousAttachmentFileIds = this.getAttachmentFileIds(previousAttachments);
    const nextAttachmentFileIds = this.getAttachmentFileIds(attachments);
    if (this.areAttachmentFileIdsEqual(previousAttachmentFileIds, nextAttachmentFileIds)) {
      return;
    }

    const addedAttachments = attachments.filter((attachment) => !previousAttachmentFileIds.includes(attachment.id));

    this.isDispatchAttachmentSaving = true;
    this.cdr.detectChanges();
    try {
      const updated = await this.shipmentEffects.updateShipment(this.shipmentId, {
        activeLegAttachmentFileIds: nextAttachmentFileIds,
      });

      this.deferViewMutation(() => {
        this.shipment = updated;
        this.dispatchForm.patchValue(
          {
            attachments: updated.attachments,
          },
          { emitEvent: false },
        );
      });
    } catch (exception: unknown) {
      await this.deleteUploadedFiles(addedAttachments);
      this.restoreDispatchAttachmentFiles(previousAttachments);
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update dispatch attachments."), "error");
    } finally {
      this.deferViewMutation(() => {
        this.isDispatchAttachmentSaving = false;
      });
    }
  }

  async saveDispatchDetails(): Promise<void> {
    if (!this.shipmentId || !this.canEditDispatchDocumentation || this.shipment?.status === "Packed" || this.isSaving) {
      return;
    }

    this.isSaving = true;
    this.cdr.detectChanges();
    try {
      const updated = await this.shipmentEffects.updateShipment(this.shipmentId, {
        activeLegNote: this.dispatchForm.value.note?.trim() ?? "",
      });

      this.deferViewMutation(() => {
        this.hydrate(updated);
        this.syncFormMode();
        this.activeStage = "dispatch";
        this.toastEffects.show("Dispatch details updated.", "success");
      });
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to update dispatch details."), "error");
    } finally {
      this.deferViewMutation(() => {
        this.isSaving = false;
      });
    }
  }

  deliverShipment(): void {
    if (!this.shipmentId || !this.canDeliver) {
      return;
    }

    const isTransitArrival = this.currentLeg?.type === "Transit";
    this.confirmModalEffects.open({
      title: (() => {
        if (isTransitArrival) {
          return "Confirm Transit Arrival";
        }
        return "Confirm Delivery";
      })(),
      message: (() => {
        if (isTransitArrival) {
          return `Mark shipment "${this.shipment?.shipmentNo}" as received at the transit drop point?`;
        }
        return `Mark shipment "${this.shipment?.shipmentNo}" as delivered at the final destination?`;
      })(),
      confirmText: (() => {
        if (isTransitArrival) {
          return "Confirm Transit Arrival";
        }
        return "Confirm Delivery";
      })(),
      cancelText: "Not Yet",
      variant: "primary",
      closeOnOverlay: true,
      onConfirm: async () => {
        try {
          const updated = await this.shipmentEffects.deliverShipment(this.shipmentId!, {
            receivedBy: this.deliverForm.value.receivedBy?.trim() ?? "",
            note: this.deliverForm.value.note?.trim() ?? "",
          });

          setTimeout(() => {
            this.hydrate(updated);
            this.syncFormMode();
            this.activeStage = this.resolveInitialStage(updated);
            this.toastEffects.show(
              (() => {
                if (isTransitArrival) {
                  return "Transit arrival confirmed.";
                }
                return "Shipment marked as delivered.";
              })(),
              "success",
            );
            this.cdr.detectChanges();
          }, 0);
        } catch (exception: unknown) {
          this.toastEffects.show(getBackendErrorMessage(exception, "Unable to confirm delivery."), "error");
        }
      },
    });
  }

  async openLabelPdf(box: ShipmentBox): Promise<void> {
    if (!this.shipment || !this.canOpenLabel(box)) {
      return;
    }

    try {
      const blob = await this.shipmentEffects.downloadBoxLabelPdf(this.shipment.id, box.id);
      this.openPdfBlob(blob);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to open label PDF."), "error");
    }
  }

  async openAllLabelsPdf(): Promise<void> {
    if (!this.shipment || !this.canOpenAllLabels()) {
      return;
    }

    try {
      const blob = await this.shipmentEffects.downloadShipmentLabelsPdf(this.shipment.id);
      this.openPdfBlob(blob);
    } catch (exception: unknown) {
      this.toastEffects.show(getBackendErrorMessage(exception, "Unable to open shipment labels PDF."), "error");
    }
  }

  formatAddress(address?: LocationAddress | null): string {
    return formatShipmentAddress(address) || "-";
  }

  private isLocationAddressEmpty(address: LocationAddress): boolean {
    return isShipmentAddressEmpty(address);
  }

  private async loadShipment(id: string): Promise<void> {
    const shipment = await this.shipmentEffects.getShipmentById(id);
    if (!shipment) {
      this.toastEffects.show("Shipment not found.", "error");
      this.goBack();
      return;
    }

    setTimeout(() => {
      this.hydrate(shipment);
      this.syncFormMode();
      this.activeStage = this.resolveInitialStage(shipment);
      this.isReady = true;
      this.cdr.detectChanges();
    }, 0);
  }

  private hydrate(shipment: ShipmentEntity): void {
    this.shipment = shipment;
    this.syncSavedBoxesViewModel(shipment);
    this.expandedRequestIds = new Set(
      Array.from(this.expandedRequestIds).filter((requestId) =>
        (shipment.requests ?? []).some((request) => request.requestId === requestId),
      ),
    );
    this.hasTransitStop = !!this.transitLeg;
    this.finalSelectionTouched = false;
    this.transitSelectionTouched = false;
    const requestSeed = shipment.requests?.[0];
    const seededFinal = createSeededDestinationSection(
      {
        partnerId: shipment.finalPartnerId ?? requestSeed?.partnerId,
        partnerName: shipment.destinationInstitutionName ?? requestSeed?.partnerName,
        partnerPhone: undefined,
        partnerAddress: shipment.destinationAddress,
        recipientId: shipment.finalRecipientTeamLeaderId ?? requestSeed?.teamLeaderId,
        recipientName: shipment.recipientName ?? requestSeed?.teamLeaderName,
        recipientPhone: shipment.recipientPhone ?? requestSeed?.teamLeaderPhone,
        recipientAddress: undefined,
        snapshotPhone: shipment.recipientPhone,
        snapshotAddress: shipment.destinationAddress,
      },
      {
        addressSource: "snapshot",
        phoneSource: "snapshot",
      },
    );
    this.selectedFinalPartnerId = seededFinal.selectedPartnerId;
    this.selectedFinalRecipientId = seededFinal.selectedRecipientId;
    this.selectedFinalPartner = seededFinal.selectedPartner;
    this.selectedFinalRecipient = seededFinal.selectedRecipient;
    this.finalPhoneValue = seededFinal.phoneValue;
    this.finalAddressValue = seededFinal.addressValue;
    this.finalRecipientsAvailable = seededFinal.recipientsAvailable;

    const seededTransit = createSeededDestinationSection(
      {
        partnerId: this.transitLeg?.transitPartnerId,
        partnerName: this.transitLeg?.destinationInstitutionName,
        partnerPhone: undefined,
        partnerAddress: this.transitLeg?.destinationAddress,
        recipientId: this.transitLeg?.transitRecipientTeamLeaderId,
        recipientName: this.transitLeg?.recipientName,
        recipientPhone: this.transitLeg?.recipientPhone,
        recipientAddress: this.transitLeg?.destinationAddress,
        snapshotPhone: this.transitLeg?.recipientPhone,
        snapshotAddress: this.transitLeg?.destinationAddress,
      },
      {
        addressSource: "snapshot",
        phoneSource: "snapshot",
      },
    );
    this.selectedTransitPartnerId = seededTransit.selectedPartnerId;
    this.selectedTransitRecipientId = seededTransit.selectedRecipientId;
    this.selectedTransitPartner = seededTransit.selectedPartner;
    this.selectedTransitRecipient = seededTransit.selectedRecipient;
    this.transitPhoneValue = seededTransit.phoneValue;
    this.transitAddressValue = seededTransit.addressValue;
    this.transitRecipientsAvailable = (() => {
      if (this.transitLeg) {
        return seededTransit.recipientsAvailable;
      }
      return null;
    })();
    this.form.patchValue({
      note: shipment.note ?? "",
    });
    this.transitForm.patchValue({
      transitPartnerId: this.selectedTransitPartnerId,
      transitRecipientTeamLeaderId: this.selectedTransitRecipientId,
    });
    if (this.selectedFinalPartnerId) {
      if (this.selectedFinalPartner) {
        this.finalPartnerMap.set(this.selectedFinalPartnerId, this.selectedFinalPartner);
      }
      this.finalPartnerOptions = this.mergeUniqueOptions(this.finalPartnerOptions, [
        {
          value: this.selectedFinalPartnerId,
          label: shipment.destinationInstitutionName ?? this.selectedFinalPartnerId,
        },
      ]);
    }
    if (this.selectedFinalRecipientId) {
      if (this.selectedFinalRecipient) {
        this.finalRecipientMap.set(this.selectedFinalRecipientId, this.selectedFinalRecipient);
      }
      this.finalRecipientOptions = this.mergeUniqueOptions(this.finalRecipientOptions, [
        {
          value: this.selectedFinalRecipientId,
          label: shipment.recipientName ?? this.selectedFinalRecipientId,
        },
      ]);
    }
    if (this.selectedTransitPartnerId) {
      if (this.selectedTransitPartner) {
        this.transitPartnerMap.set(this.selectedTransitPartnerId, this.selectedTransitPartner);
      }
      this.transitPartnerOptions = this.mergeUniqueOptions(this.transitPartnerOptions, [
        {
          value: this.selectedTransitPartnerId,
          label: this.transitLeg?.destinationInstitutionName ?? this.selectedTransitPartnerId,
        },
      ]);
    }
    if (this.selectedTransitRecipientId) {
      if (this.selectedTransitRecipient) {
        this.transitRecipientMap.set(this.selectedTransitRecipientId, this.selectedTransitRecipient);
      }
      this.transitRecipientOptions = this.mergeUniqueOptions(this.transitRecipientOptions, [
        {
          value: this.selectedTransitRecipientId,
          label: this.transitLeg?.recipientName ?? this.selectedTransitRecipientId,
        },
      ]);
    }
    this.dispatchForm.patchValue({
      vendorId: this.toSelectedAutocompleteValue(this.getActiveDispatchVendorId(shipment)),
      finalLegVendorId: this.toSelectedAutocompleteValue(this.getShipmentFinalLeg(shipment)?.vendorId),
      waybillNo: shipment.waybillNo ?? "",
      note: shipment.activeLeg?.note ?? "",
      attachments: shipment.attachments ?? [],
    });
    this.selectedDispatchVendorId = this.toSelectedAutocompleteValue(this.getActiveDispatchVendorId(shipment));
    this.selectedFinalLegVendorId = this.toSelectedAutocompleteValue(this.getShipmentFinalLeg(shipment)?.vendorId);
    this.deliverForm.patchValue({
      receivedBy: shipment.receivedBy ?? "",
      note: shipment.activeLeg?.note ?? "",
    });
    this.seedVendorOptionsFromShipment(shipment);
    this.rebuildMeasurementDrafts(shipment);
    if (this.editingBoxId || this.isCreatingBoxDraft) {
      let currentEditingBox: ShipmentBox | undefined;
      if (this.editingBoxId) {
        currentEditingBox = shipment.boxes?.find((box) => box.id === this.editingBoxId);
      }
      this.buildBoxEditor(currentEditingBox);
    }
  }

  async searchShipmentVendors(term: string): Promise<void> {
    const response = await this.vendorApi.searchVendors({ search: term, type: VendorType.Shipment, page: 1, limit: 50 });
    const nextVendorOptions = this.mergeUniqueOptions(
      this.vendorOptions,
      response.data.map((item) => ({ value: item.id, label: item.name })),
    );

    this.deferViewMutation(() => {
      this.vendorOptions = nextVendorOptions;
    });
  }

  async searchTransitPartners(term: string): Promise<void> {
    const response = await this.partnerApi.searchPartners({ search: term, page: 1, limit: 50 });
    response.data.forEach((partner) => {
      this.transitPartnerMap.set(partner.id, partner);
    });

    this.deferViewMutation(() => {
      this.transitPartnerOptions = this.mergeUniqueOptions(
        this.transitPartnerOptions,
        response.data.map((partner) => ({ value: partner.id, label: partner.name })),
      );
    });
  }

  async searchFinalPartners(term: string): Promise<void> {
    const response = await this.partnerApi.searchPartners({ search: term, page: 1, limit: 50 });
    response.data.forEach((partner) => {
      this.finalPartnerMap.set(partner.id, partner);
    });

    this.deferViewMutation(() => {
      this.finalPartnerOptions = this.mergeUniqueOptions(
        this.finalPartnerOptions,
        response.data.map((partner) => ({ value: partner.id, label: partner.name })),
      );
    });
  }

  async onFinalPartnerChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const finalPartnerId = value.toString();
    this.finalSelectionTouched = true;
    this.selectedFinalPartnerId = finalPartnerId;
    if (!finalPartnerId) {
      this.resetFinalSelection();
      return;
    }

    this.selectedFinalRecipientId = "";
    this.selectedFinalRecipient = null;
    this.finalRecipientOptions = [];
    this.finalRecipientsAvailable = null;
    this.finalPhoneValue = "";

    const existingPartner = this.finalPartnerMap.get(finalPartnerId);
    if (existingPartner) {
      this.selectedFinalPartner = existingPartner;
      this.finalAddressValue = existingPartner.address || this.createEmptyAddress();
      await this.loadFinalRecipientsForPartner("");
      return;
    }

    const partner = await this.partnerApi.getPartnerById(finalPartnerId);
    if (!partner) {
      this.selectedFinalPartner = null;
      this.toastEffects.show("Final partner not found.", "error");
      return;
    }

    this.finalPartnerMap.set(partner.id, partner);
    this.finalPartnerOptions = this.mergeUniqueOptions(this.finalPartnerOptions, [{ value: partner.id, label: partner.name }]);
    this.selectedFinalPartner = partner;
    this.finalAddressValue = partner.address || this.createEmptyAddress();
    await this.loadFinalRecipientsForPartner("");
  }

  async searchFinalRecipients(term: string): Promise<void> {
    await this.loadFinalRecipientsForPartner(term);
  }

  async onFinalRecipientChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const finalRecipientTeamLeaderId = value.toString();
    this.finalSelectionTouched = true;
    this.selectedFinalRecipientId = finalRecipientTeamLeaderId;
    if (!finalRecipientTeamLeaderId) {
      this.selectedFinalRecipient = null;
      this.finalPhoneValue = "";
      return;
    }

    const existingRecipient = this.finalRecipientMap.get(finalRecipientTeamLeaderId);
    if (existingRecipient) {
      this.selectedFinalRecipient = existingRecipient;
      this.finalPhoneValue = existingRecipient.phone || "";
      return;
    }

    const recipient = await this.teamLeaderApi.getTeamLeaderById(finalRecipientTeamLeaderId);
    if (!recipient) {
      this.selectedFinalRecipient = null;
      this.toastEffects.show("Final recipient not found.", "error");
      return;
    }

    this.finalRecipientMap.set(recipient.id, recipient);
    this.finalRecipientOptions = this.mergeUniqueOptions(this.finalRecipientOptions, [
      { value: recipient.id, label: recipient.name },
    ]);
    this.selectedFinalRecipient = recipient;
    this.finalPhoneValue = recipient.phone || "";
  }

  onFinalPhoneChange(value: string): void {
    this.finalSelectionTouched = true;
    this.finalPhoneValue = value;
  }

  onFinalAddressChange(value: LocationAddress): void {
    this.finalSelectionTouched = true;
    this.finalAddressValue = value;
  }

  async onTransitPartnerChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const transitPartnerId = value.toString();
    this.transitSelectionTouched = true;
    this.selectedTransitPartnerId = transitPartnerId;
    this.transitForm.patchValue({ transitPartnerId, transitRecipientTeamLeaderId: "" }, { emitEvent: false });
    if (!transitPartnerId) {
      this.resetTransitSelection();
      return;
    }

    this.selectedTransitRecipientId = "";
    this.selectedTransitRecipient = null;
    this.transitRecipientOptions = [];
    this.transitRecipientsAvailable = null;
    this.transitPhoneValue = "";
    this.transitAddressValue = this.createEmptyAddress();

    const existingPartner = this.transitPartnerMap.get(transitPartnerId);
    if (existingPartner) {
      this.selectedTransitPartner = existingPartner;
      await this.loadTransitRecipientsForPartner("");
      return;
    }

    const partner = await this.partnerApi.getPartnerById(transitPartnerId);
    if (!partner) {
      this.selectedTransitPartner = null;
      this.toastEffects.show("Transit partner not found.", "error");
      return;
    }

    this.transitPartnerMap.set(partner.id, partner);
    this.transitPartnerOptions = this.mergeUniqueOptions(this.transitPartnerOptions, [{ value: partner.id, label: partner.name }]);
    this.selectedTransitPartner = partner;
    await this.loadTransitRecipientsForPartner("");
  }

  async searchTransitRecipients(term: string): Promise<void> {
    await this.loadTransitRecipientsForPartner(term);
  }

  async onTransitRecipientChange(value: string | number | (string | number)[]): Promise<void> {
    if (Array.isArray(value)) {
      return;
    }

    const transitRecipientTeamLeaderId = value.toString();
    this.transitSelectionTouched = true;
    this.selectedTransitRecipientId = transitRecipientTeamLeaderId;
    this.transitForm.patchValue({ transitRecipientTeamLeaderId }, { emitEvent: false });
    if (!transitRecipientTeamLeaderId) {
      this.selectedTransitRecipient = null;
      return;
    }

    const existingRecipient = this.transitRecipientMap.get(transitRecipientTeamLeaderId);
    if (existingRecipient) {
      this.selectedTransitRecipient = existingRecipient;
      this.transitPhoneValue = existingRecipient.phone || "";
      this.transitAddressValue = existingRecipient.address || this.createEmptyAddress();
      return;
    }

    const recipient = await this.teamLeaderApi.getTeamLeaderById(transitRecipientTeamLeaderId);
    if (!recipient) {
      this.selectedTransitRecipient = null;
      this.toastEffects.show("Transit recipient not found.", "error");
      return;
    }

    this.transitRecipientMap.set(recipient.id, recipient);
    this.transitRecipientOptions = this.mergeUniqueOptions(this.transitRecipientOptions, [
      { value: recipient.id, label: recipient.name },
    ]);
    this.selectedTransitRecipient = recipient;
    this.transitPhoneValue = recipient.phone || "";
    this.transitAddressValue = recipient.address || this.createEmptyAddress();
  }

  onDispatchVendorChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }

    const selectedVendorId = value.toString();
    this.dispatchForm.patchValue({ vendorId: selectedVendorId }, { emitEvent: false });
    this.selectedDispatchVendorId = selectedVendorId;
  }

  onFinalLegVendorChange(value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }

    const selectedVendorId = value.toString();
    this.dispatchForm.patchValue({ finalLegVendorId: selectedVendorId }, { emitEvent: false });
    this.selectedFinalLegVendorId = selectedVendorId;
  }

  getDispatchVendorLabel(): string {
    return (
      this.shipment?.activeLeg?.vendor?.name?.trim() ||
      this.shipment?.vendor?.name?.trim() ||
      this.getVendorOptionLabel(this.shipment?.vendorId) ||
      "-"
    );
  }

  getFinalLegVendorLabel(): string {
    return this.finalLeg?.vendor?.name?.trim() || this.getVendorOptionLabel(this.finalLeg?.vendorId) || "-";
  }

  getLegVendorLabel(leg?: ShipmentLeg | null): string {
    return leg?.vendor?.name?.trim() || this.getVendorOptionLabel(leg?.vendorId) || "-";
  }

  getLegWaybillLabel(leg?: ShipmentLeg | null): string {
    return leg?.waybillNo?.trim() || "-";
  }

  getLegStatusLabel(leg?: ShipmentLeg | null): string {
    if (!leg) {
      return "-";
    }

    if (this.currentLeg?.id === leg.id && leg.status === "Pending") {
      return "Active";
    }

    if (leg.status === "Dispatched") {
      return "In Transit";
    }

    return leg.status;
  }

  getDispatchWaybillLabel(): string {
    return this.shipment?.waybillNo?.trim() || "-";
  }

  private getDispatchVendorIdForPayload(): string {
    if (this.isFinalLegDispatch) {
      return this.finalLeg?.vendorId?.trim() || "";
    }

    return this.dispatchForm.getRawValue().vendorId?.trim() ?? "";
  }

  private getActiveDispatchVendorId(shipment: ShipmentEntity): string | null | undefined {
    return shipment.activeLeg?.vendorId ?? shipment.vendorId;
  }

  private getShipmentFinalLeg(shipment: ShipmentEntity): ShipmentLeg | null {
    return shipment.legs?.find((leg) => leg.type === "Final") ?? shipment.activeLeg ?? null;
  }

  private getVendorOptionLabel(vendorId?: string | null): string {
    if (!vendorId) {
      return "";
    }

    return this.vendorOptions.find((option) => option.value.toString() === vendorId)?.label?.trim() || "";
  }

  private seedVendorOptionsFromShipment(shipment: ShipmentEntity): void {
    const seededVendors: AutocompleteOption[] = [];
    if (shipment.vendorId) {
      seededVendors.push({
        value: shipment.vendorId,
        label: shipment.vendor?.name?.trim() || shipment.vendorId,
      });
    }
    (shipment.legs ?? []).forEach((leg) => {
      if (leg.vendorId) {
        seededVendors.push({
          value: leg.vendorId,
          label: leg.vendor?.name?.trim() || leg.vendorId,
        });
      }
    });
    this.vendorOptions = this.mergeUniqueOptions(this.vendorOptions, seededVendors);
  }

  private mergeUniqueOptions(base: AutocompleteOption[], incoming: AutocompleteOption[]): AutocompleteOption[] {
    const map = new Map<string | number, AutocompleteOption>();
    [...base, ...incoming].forEach((option) => {
      if (!option.value) {
        return;
      }
      map.set(option.value, option);
    });

    return Array.from(map.values());
  }

  private getAttachmentFileIds(files: UploadedFile[]): string[] {
    return files.map((file) => file.id).filter((id) => !!id);
  }

  private areAttachmentFileIdsEqual(currentValue: string[], nextValue: string[]): boolean {
    if (currentValue.length !== nextValue.length) {
      return false;
    }

    return currentValue.every((currentId, index) => currentId === nextValue[index]);
  }

  private restoreDispatchAttachmentFiles(files: UploadedFile[]): void {
    this.deferViewMutation(() => {
      this.dispatchForm.patchValue(
        {
          attachments: files,
        },
        { emitEvent: false },
      );
    });
  }

  private async deleteUploadedFiles(files: UploadedFile[]): Promise<void> {
    for (const file of files) {
      if (!file.name?.trim()) {
        continue;
      }

      try {
        await this.fileApi.deleteFile(file.name);
      } catch {
        continue;
      }
    }
  }

  private deferViewMutation(callback: () => void): void {
    setTimeout(() => {
      callback();
      this.cdr.detectChanges();
    }, 0);
  }

  private openPdfBlob(blob: Blob): void {
    const objectUrl = URL.createObjectURL(blob);
    const openedWindow = window.open(objectUrl, "_blank", "noopener,noreferrer");
    if (!openedWindow) {
      URL.revokeObjectURL(objectUrl);
      this.toastEffects.show("Popup blocked. Please allow popups and try again.", "error");
      return;
    }

    setTimeout(() => {
      URL.revokeObjectURL(objectUrl);
    }, 60_000);
  }

  private toSelectedAutocompleteValue(value: string | null | undefined): string {
    if (typeof value === "string") {
      return value;
    }

    return "";
  }

  getCurrentTransitSnapshot(): {
    partnerName: string;
    recipientName: string;
    recipientPhone: string;
    destinationAddress: LocationAddress;
  } | null {
    const formSnapshot = buildShipmentDestinationSnapshot({
      selectedPartner: this.selectedTransitPartner,
      selectedRecipient: this.selectedTransitRecipient,
      phoneValue: this.transitPhoneValue,
      addressValue: this.transitAddressValue,
    });
    if (formSnapshot) {
      return formSnapshot;
    }

    if (this.hasRouteStarted && this.transitLeg) {
      return {
        partnerName: this.transitLeg.destinationInstitutionName || "-",
        recipientName: this.transitLeg.recipientName || "-",
        recipientPhone: this.transitLeg.recipientPhone || "",
        destinationAddress: this.transitLeg.destinationAddress,
      };
    }

    return null;
  }

  getCurrentFinalSnapshot(): {
    partnerName: string;
    recipientName: string;
    recipientPhone: string;
    destinationAddress: LocationAddress;
  } | null {
    const formSnapshot = buildShipmentDestinationSnapshot({
      selectedPartner: this.selectedFinalPartner,
      selectedRecipient: this.selectedFinalRecipient,
      phoneValue: this.finalPhoneValue,
      addressValue: this.finalAddressValue,
    });
    if (formSnapshot) {
      return formSnapshot;
    }

    if (this.hasRouteStarted && this.shipment) {
      const finalLeg = this.finalLeg;
      return {
        partnerName: finalLeg?.destinationInstitutionName || this.shipment.destinationInstitutionName || "-",
        recipientName: finalLeg?.recipientName || this.shipment.recipientName || "-",
        recipientPhone: finalLeg?.recipientPhone || this.shipment.recipientPhone || "",
        destinationAddress: finalLeg?.destinationAddress || this.shipment.destinationAddress,
      };
    }

    return null;
  }

  private resolveFinalPartnerPayload(): string | undefined {
    return this.selectedFinalPartnerId || undefined;
  }

  private resolveFinalRecipientPayload(): string | undefined {
    return this.selectedFinalRecipientId || undefined;
  }

  private resolveTransitPartnerPayload(): string | undefined {
    if (!this.hasTransitStop) {
      return undefined;
    }

    return this.selectedTransitPartnerId || undefined;
  }

  private resolveTransitRecipientPayload(): string | undefined {
    if (!this.hasTransitStop) {
      return undefined;
    }

    return this.selectedTransitRecipientId || undefined;
  }

  private async loadTransitRecipientsForPartner(term: string): Promise<void> {
    if (!this.selectedTransitPartnerId) {
      this.transitRecipientOptions = [];
      this.transitRecipientsAvailable = null;
      return;
    }

    const response = await this.teamLeaderApi.searchTeamLeaders({
      search: term,
      partnerIds: [this.selectedTransitPartnerId],
      page: 1,
      limit: 50,
    });

    response.data.forEach((teamLeader) => {
      this.transitRecipientMap.set(teamLeader.id, teamLeader);
    });

    this.deferViewMutation(() => {
      this.transitRecipientOptions = this.mergeUniqueOptions(
        this.transitRecipientOptions,
        response.data.map((teamLeader) => ({ value: teamLeader.id, label: teamLeader.name })),
      );
      this.transitRecipientsAvailable = response.data.length > 0;
    });
  }

  private async loadFinalRecipientsForPartner(term: string): Promise<void> {
    if (!this.selectedFinalPartnerId) {
      this.finalRecipientOptions = [];
      this.finalRecipientsAvailable = null;
      return;
    }

    const response = await this.teamLeaderApi.searchTeamLeaders({
      search: term,
      partnerIds: [this.selectedFinalPartnerId],
      page: 1,
      limit: 50,
    });

    response.data.forEach((teamLeader) => {
      this.finalRecipientMap.set(teamLeader.id, teamLeader);
    });

    this.deferViewMutation(() => {
      this.finalRecipientOptions = this.mergeUniqueOptions(
        this.finalRecipientOptions,
        response.data.map((teamLeader) => ({ value: teamLeader.id, label: teamLeader.name })),
      );
      this.finalRecipientsAvailable = response.data.length > 0;
    });
  }

  private resetTransitSelection(): void {
    const emptyTransit = createEmptyDestinationSection();
    this.selectedTransitPartnerId = emptyTransit.selectedPartnerId;
    this.selectedTransitPartner = emptyTransit.selectedPartner;
    this.selectedTransitRecipientId = emptyTransit.selectedRecipientId;
    this.selectedTransitRecipient = emptyTransit.selectedRecipient;
    this.transitPhoneValue = emptyTransit.phoneValue;
    this.transitRecipientOptions = [];
    this.transitRecipientsAvailable = emptyTransit.recipientsAvailable;
    this.transitAddressValue = emptyTransit.addressValue;
  }

  private resetFinalSelection(): void {
    const emptyFinal = createEmptyDestinationSection();
    this.selectedFinalPartnerId = emptyFinal.selectedPartnerId;
    this.selectedFinalPartner = emptyFinal.selectedPartner;
    this.selectedFinalRecipientId = emptyFinal.selectedRecipientId;
    this.selectedFinalRecipient = emptyFinal.selectedRecipient;
    this.finalRecipientOptions = [];
    this.finalRecipientsAvailable = emptyFinal.recipientsAvailable;
    this.finalPhoneValue = emptyFinal.phoneValue;
    this.finalAddressValue = emptyFinal.addressValue;
  }

  private syncFormMode(): void {
    if (this.canEditSnapshot) {
      this.form.enable({ emitEvent: false });
      this.transitForm.enable({ emitEvent: false });
      this.boxForm.enable({ emitEvent: false });
    } else {
      this.form.disable({ emitEvent: false });
      this.transitForm.disable({ emitEvent: false });
      this.boxForm.disable({ emitEvent: false });
    }

    if (this.shipment?.status === "Packed") {
      this.dispatchForm.enable({ emitEvent: false });
      if (this.isFinalLegDispatch) {
        this.dispatchForm.get("vendorId")?.disable({ emitEvent: false });
      }
      if (!this.isTransitFirstDispatch) {
        this.dispatchForm.get("finalLegVendorId")?.disable({ emitEvent: false });
      }
    } else if (this.shipment?.status === "Dispatched" || this.shipment?.status === "Delivered") {
      this.dispatchForm.enable({ emitEvent: false });
      this.dispatchForm.get("vendorId")?.disable({ emitEvent: false });
      this.dispatchForm.get("finalLegVendorId")?.disable({ emitEvent: false });
      this.dispatchForm.get("waybillNo")?.disable({ emitEvent: false });
      this.dispatchForm.get("attachments")?.enable({ emitEvent: false });
      this.dispatchForm.get("note")?.enable({ emitEvent: false });
    } else {
      this.dispatchForm.disable({ emitEvent: false });
    }

    if (this.shipment?.status === "Dispatched") {
      this.deliverForm.enable({ emitEvent: false });
    } else {
      this.deliverForm.disable({ emitEvent: false });
    }
  }

  private buildBoxEditor(box?: ShipmentBox): void {
    if (!this.shipment) {
      return;
    }

    const currentBox = box ?? null;
    this.editingBoxId = currentBox?.id ?? null;
    this.allocationRows = this.buildAllocationRows(currentBox?.id ?? null);
    this.allocations.clear();

    for (const row of this.allocationRows) {
      this.allocations.push(
        this.fb.group({
          qty: [row.currentEditingQty || 0, [Validators.min(0)]],
        }),
      );
    }

    this.boxForm.patchValue({
      sequenceNo: currentBox?.sequenceNo ?? (this.shipment.boxes?.length ?? 0) + 1,
      note: currentBox?.note ?? "",
    });
  }

  private findSavedBox(
    shipment: ShipmentEntity,
    previousEditingBoxId: string | null,
    previousBoxIds: Set<string>,
  ): ShipmentBox | null {
    if (previousEditingBoxId) {
      return shipment.boxes?.find((box) => box.id === previousEditingBoxId) ?? null;
    }

    const newlyCreatedBox = shipment.boxes?.find((box) => !previousBoxIds.has(box.id));
    if (newlyCreatedBox) {
      return newlyCreatedBox;
    }

    return shipment.boxes?.at(-1) ?? null;
  }

  private buildAllocationRows(editingBoxId: string | null): AllocationRow[] {
    if (!this.shipment) {
      return [];
    }

    const currentBox = this.shipment.boxes?.find((box) => box.id === editingBoxId);

    return (this.shipment.requests ?? []).flatMap((request) =>
      (request.items ?? [])
        .map((item) => {
          const editingQty =
            currentBox?.items?.find((boxItem) => boxItem.requestId === request.requestId && boxItem.materialId === item.materialId)
              ?.qty ?? 0;

          const availableQty = Math.max(0, Number(item.qty ?? 0) - (Number(item.boxedQty ?? 0) - Number(editingQty)));

          return {
            requestId: request.requestId,
            requestNo: request.requestNo,
            teamLeaderName: request.teamLeaderName,
            partnerName: request.partnerName,
            materialId: item.materialId,
            materialName: item.materialName,
            materialSku: item.materialSku,
            requestedQty: Number(item.qty ?? 0),
            boxedQty: Number(item.boxedQty ?? 0),
            dispatchedQty: Number(item.dispatchedQty ?? 0),
            deliveredQty: Number(item.deliveredQty ?? 0),
            availableQty,
            currentEditingQty: Number(editingQty),
          };
        })
        .filter((row) => {
          if (!editingBoxId) {
            return row.availableQty > 0;
          }

          return row.availableQty > 0 || row.currentEditingQty > 0;
        }),
    );
  }

  private createEmptyAddress(): LocationAddress {
    return createEmptyShipmentAddress();
  }

  private resolveInitialStage(shipment: ShipmentEntity): ShipmentStage {
    const queryStage = this.route.snapshot.queryParamMap.get("stage") as ShipmentStage | null;
    if (queryStage && this.isStageEnabledForShipment(shipment, queryStage)) {
      return queryStage;
    }

    if (shipment.status === "Dispatched") {
      return (() => {
        if (this.isFinalDeliveryStageAvailableForShipment(shipment)) {
          return "delivery";
        }
        return "dispatch";
      })();
    }
    if (shipment.status === "Delivered") {
      return "delivery";
    }
    if (shipment.status === "Packed" && (shipment.legs ?? []).some((leg) => leg.status !== "Pending")) {
      return "dispatch";
    }
    if (shipment.status === "Packed" && shipment.canDispatch) {
      return "dispatch";
    }
    if ((shipment.boxes ?? []).length > 0 || shipment.status === "Draft") {
      return "pack";
    }
    return "snapshot";
  }

  private isStageEnabledForShipment(shipment: ShipmentEntity, stage: ShipmentStage): boolean {
    const routeStarted = (shipment.legs ?? []).some((leg) => leg.status !== "Pending");

    if (shipment.status === "Draft") {
      return stage === "snapshot" || stage === "pack";
    }

    if (shipment.status === "Packed") {
      if (routeStarted) {
        return stage === "dispatch";
      }
      return stage === "snapshot" || stage === "pack" || stage === "dispatch";
    }

    if (shipment.status === "Dispatched" || shipment.status === "Delivered") {
      if (stage === "delivery") {
        return this.isFinalDeliveryStageAvailableForShipment(shipment);
      }
      if (this.isTransitArrivalPendingForShipment(shipment)) {
        return stage === "dispatch";
      }
      return true;
    }

    return false;
  }

  private isTransitArrivalPendingForShipment(shipment: ShipmentEntity): boolean {
    const activeLeg = shipment.activeLeg ?? shipment.legs?.find((leg) => leg.status !== "Delivered") ?? null;
    return (
      this.isTransitRouteShipment(shipment) &&
      shipment.status === "Dispatched" &&
      activeLeg?.type === "Transit" &&
      activeLeg.status === "Dispatched"
    );
  }

  private isFinalDeliveryStageAvailableForShipment(shipment: ShipmentEntity): boolean {
    if (shipment.status === "Delivered") {
      return true;
    }

    if (shipment.status !== "Dispatched") {
      return false;
    }

    if (!this.isTransitRouteShipment(shipment)) {
      return true;
    }

    const activeLeg = shipment.activeLeg ?? shipment.legs?.find((leg) => leg.status !== "Delivered") ?? null;
    return activeLeg?.type === "Final" && activeLeg.status === "Dispatched";
  }

  private isTransitRouteShipment(shipment: ShipmentEntity): boolean {
    const legs = shipment.legs ?? [];
    return legs.length > 1 && legs.some((leg) => leg.type === "Transit") && legs.some((leg) => leg.type === "Final");
  }

  private getMeasurementDraft(box: ShipmentBox): MeasurementDraft {
    return (
      this.measurementDrafts.get(box.id) ?? {
        lengthCm: Number(box.lengthCm ?? 0) || 0,
        widthCm: Number(box.widthCm ?? 0) || 0,
        heightCm: Number(box.heightCm ?? 0) || 0,
        weightKg: Number(box.weightKg ?? 0) || 0,
      }
    );
  }

  private getMeasurementDraftValue(box: ShipmentBox, key: keyof MeasurementDraft): number | null {
    return this.getMeasurementDraft(box)[key];
  }

  private setMeasurementDraftValue(box: ShipmentBox, key: keyof MeasurementDraft, value: string | number | null): void {
    const current = this.getMeasurementDraft(box);
    let nextValue: number | null;
    if (value === null || value === "" || Number.isNaN(Number(value))) {
      nextValue = null;
    } else {
      nextValue = Number(value);
    }

    this.measurementDrafts.set(box.id, {
      ...current,
      [key]: nextValue,
    });
  }

  private getSavedBoxMeasurementStatus(box: ShipmentBox): string {
    if (this.isBoxMeasured(box)) {
      return "Measured";
    }
    return "Needs Measurement";
  }

  private getSavedBoxMeasurementStatusClass(box: ShipmentBox): string {
    if (this.isBoxMeasured(box)) {
      return "app-table__chip app-table__chip--info";
    }
    return "app-table__chip app-table__chip--warning";
  }

  private canSaveMeasurements(box: ShipmentBox): boolean {
    const draft = this.getMeasurementDraft(box);
    return [draft.lengthCm, draft.widthCm, draft.heightCm, draft.weightKg].every((value) => Number(value ?? 0) > 0);
  }

  private formatCubicPreview(box: ShipmentBox): string {
    return this.getCubicM3PreviewFor(box).toFixed(3);
  }

  private rebuildMeasurementDrafts(shipment: ShipmentEntity): void {
    const nextDrafts = new Map<string, MeasurementDraft>();
    for (const box of shipment.boxes ?? []) {
      const existingDraft = this.measurementDrafts.get(box.id);
      nextDrafts.set(box.id, {
        lengthCm: existingDraft?.lengthCm ?? (Number(box.lengthCm ?? 0) || 0),
        widthCm: existingDraft?.widthCm ?? (Number(box.widthCm ?? 0) || 0),
        heightCm: existingDraft?.heightCm ?? (Number(box.heightCm ?? 0) || 0),
        weightKg: existingDraft?.weightKg ?? (Number(box.weightKg ?? 0) || 0),
      });
    }
    this.measurementDrafts = nextDrafts;
  }

  private syncSavedBoxesViewModel(shipment: ShipmentEntity): void {
    this.savedBoxesData = [...(shipment.boxes ?? [])];
    this.hasSavedBoxes = this.savedBoxesData.length > 0;

    let pages = 0;
    if (this.hasSavedBoxes) {
      pages = 1;
    }

    this.savedBoxesPagination = {
      page: 1,
      limit: Math.max(this.savedBoxesData.length, 1),
      total: this.savedBoxesData.length,
      pages,
    };
  }
}
